import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pw_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem("pw_cart", JSON.stringify(cart));
  }, [cart]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    // Skip auth probe for guests to avoid noisy 401s in the console.
    if (!localStorage.getItem("pw_has_session")) {
      setAuthLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          qty,
          image: product.images?.[0] || "",
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
  };

  const updateQty = (product_id, qty) => {
    if (qty < 1) return;
    setCart((prev) => prev.map((i) => (i.product_id === product_id ? { ...i, qty } : i)));
  };
  const removeFromCart = (product_id) =>
    setCart((prev) => prev.filter((i) => i.product_id !== product_id));
  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const logout = async () => {
    await api.post("/auth/logout");
    localStorage.removeItem("pw_has_session");
    setUser(null);
  };

  return (
    <StoreContext.Provider
      value={{
        cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartTotal,
        user, setUser, authLoading, checkAuth, logout,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};
